import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import { Check } from "lucide-react";

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  color: #23272e;
`;

const ResortSelect = styled.select`
  width: 100%;
  padding: 12px 2.5rem 12px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 1rem;
  background: white;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 8 10 12 14 8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1.25rem 1.25rem;
  /* For Firefox (optional, but helps) */
  &::-ms-expand {
    display: none;
  }
`;

const DropZone = styled.div`
  border: 2px dashed ${(props) => (props.$isDragActive ? "#005fcc" : "#e5e7eb")};
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background: ${(props) =>
    props.$isDragActive ? "rgba(0, 95, 204, 0.05)" : "white"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #005fcc;
    background: rgba(0, 95, 204, 0.05);
  }
`;

const DropZoneText = styled.p`
  margin: 0;
  color: #666;
  font-size: 1rem;
`;

const FileList = styled.div`
  margin-top: 20px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;

  ${(props) =>
    props.$primary
      ? `
    background: #005fcc;
    color: white;
    border: none;
    &:hover {
      background: #004999;
    }
  `
      : `
    background: white;
    color: #666;
    border: 1px solid #e5e7eb;
    &:hover {
      background: #f9fafb;
    }
  `}
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 8px;
  margin-bottom: 16px;
`;

const SuccessView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
`;

const CheckIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #28a745;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: white;
`;

const SuccessMessage = styled.div`
  font-size: 1.125rem;
  font-weight: 500;
`;

export default function UploadDialog({ resorts, onClose, onUpload }) {
  const [selectedResortId, setSelectedResortId] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newResortName, setNewResortName] = useState("");
  const [localResorts, setLocalResorts] = useState(
    Array.isArray(resorts) ? resorts : []
  );

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
    setError(""); // Clear error when new files are added
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/gpx+xml": [".gpx"],
      "text/xml": [".gpx"],
    },
  });

  const handleUpload = async () => {
    if (!selectedResortId) {
      setError("Please select a resort before uploading");
      return;
    }
    if (files.length === 0) {
      setError("Please select at least one file to upload");
      return;
    }

    setError(""); // Clear any previous errors

    // Check file sizes before uploading (Vercel has a 4.5MB payload limit)
    const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB
    const oversized = files.find((f) => f.size > MAX_FILE_SIZE);
    if (oversized) {
      setError(`File "${oversized.name}" is too large (${(oversized.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 4MB.`);
      return;
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await fetch(`/api/resort/${selectedResortId}/upload`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setSuccess(
          `Successfully uploaded ${files.length} file${files.length === 1 ? "" : "s"}`
        );
        setTimeout(() => {
          onUpload();
          onClose();
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Upload failed. Please try again.");
      }
    } catch (error) {
      setError("An error occurred during upload. Please try again.");
      console.error("Upload error:", error);
    }
  };

  const handleResortChange = (e) => {
    if (e.target.value === "__add_new__") {
      setAddingNew(true);
      setSelectedResortId("");
      setError("");
    } else {
      setAddingNew(false);
      setSelectedResortId(e.target.value);
      setError("");
    }
  };

  const handleAddNewResort = async () => {
    const trimmed = newResortName.trim();
    if (!trimmed) {
      setError("Resort name cannot be empty");
      return;
    }
    // Generate a unique key (could be improved)
    const key = trimmed.toLowerCase().replace(/\s+/g, "-");

    // Check if resort already exists
    if (localResorts.some((resort) => resort.id === key)) {
      setError("Resort already exists");
      return;
    }

    try {
      const response = await fetch(`/api/resort/${key}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed, displayName: newResortName }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Failed to create resort");
        return;
      }

      // Add new resort to the array
      const newResort = {
        id: key,
        name: trimmed,
        location: "",
        totalVisits: 0,
      };

      setLocalResorts((prev) => [...prev, newResort]);
      setSelectedResortId(key);
      setAddingNew(false);
      setNewResortName("");
      setError("");
    } catch {
      setError("Failed to create resort");
    }
  };

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        {success ? (
          <SuccessView>
            <CheckIcon>
              <Check size={24} />
            </CheckIcon>
            <SuccessMessage>{success}</SuccessMessage>
          </SuccessView>
        ) : (
          <>
            <Title>Upload GPX Files</Title>

            <ResortSelect
              value={addingNew ? "__add_new__" : selectedResortId}
              onChange={handleResortChange}
            >
              <option value="">Select a resort</option>
              {localResorts
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((resort) => (
                  <option key={resort.id} value={resort.id}>
                    {resort.name}
                  </option>
                ))}
              <option value="__add_new__">+ Add new resort…</option>
            </ResortSelect>

            {addingNew && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <input
                  type="text"
                  placeholder="New resort name"
                  value={newResortName}
                  onChange={(e) => setNewResortName(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: "1rem",
                  }}
                  autoFocus
                />
                <Button
                  type="button"
                  onClick={handleAddNewResort}
                  style={{ flex: "none", padding: "8px 16px" }}
                >
                  Add
                </Button>
              </div>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <DropZone {...getRootProps()} $isDragActive={isDragActive}>
              <input {...getInputProps()} />
              <DropZoneText>
                {isDragActive
                  ? "Drop the files here..."
                  : "Drag and drop GPX files here, or click to select files"}
              </DropZoneText>
            </DropZone>

            {files.length > 0 && (
              <FileList>
                {files.map((file, index) => (
                  <FileItem key={index}>
                    <span>{file.name}</span>
                    <button
                      onClick={() =>
                        setFiles(files.filter((_, i) => i !== index))
                      }
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </FileItem>
                ))}
              </FileList>
            )}

            <ButtonGroup>
              <Button onClick={onClose}>Cancel</Button>
              <Button $primary onClick={handleUpload}>
                Upload
              </Button>
            </ButtonGroup>
          </>
        )}
      </DialogContent>
    </DialogOverlay>
  );
}
